import React, {useContext, useEffect, useRef, useState} from "react";
import moment from "moment/moment";
import storage from "../../storage";
import Context from "../../context/Message";
import ClientAvatar from "./ClientAvatar";
import {AnonymousMessageCheckbox} from "./SendMessageForm";
import { replaceUrlsToLinks } from "../../../../platform/src/helpers/html-helpers";

const likeStorageKey = (name, id) => `${name}/message/${id}/rated`;

const ClientNickname = ({client}) => {
    const {$locale} = useContext(Context);

    const {nickname, link} = client || {};

    if (link) {
        return (<a className="nickname" target="_blank" href={link}>{nickname}</a>);
    }

    if (nickname) {
        return (<span className="nickname">{nickname}</span>);
    }

    return (<span className="nickname anonymous">{$locale.get('comment.anonymous.nickname')}</span>);
};

const LikeButton = ({active, onClick, children}) => {
    if (!onClick) {
        return null;
    }

    let className = "rating icon-rating";
    if (active) {
        className += " icon-active";
    }

    return (
        <span className={className} onClick={onClick}>{children}</span>
    );
};

const MessageTime = ({time, onClick, format = 'HH:mm DD-MM-YYYY'}) => {
    return (
        <a className="datetime" onClick={onClick}>{moment.unix(time).format(format)}</a>
    );
};

const MessageTarget = ({member}) => {
    const {$locale} = useContext(Context);

    if (!member) {
        return null;
    }

    const memberName = $locale.getName().toLowerCase() === 'en'
        ? (member.fullnameEn || member.fullnameRu)
        : member.fullnameRu;

    return (
        <span className="message-receiver">@{memberName}, </span>
    );
};

const MessageText = ({text}) => {
    const div = document.createElement('div');
    div.innerText = text;
    let html = replaceUrlsToLinks(div.innerHTML);

    return (
        <span dangerouslySetInnerHTML={{__html: html}} />
    )
};

const AnswerForm = ({parent, onSubmit, inputRef}) => {
    const {
        $locale,
        client,
        allowAnonymousMessage,
        maxMessageLength,
    } = useContext(Context);
    const [sending, setSending] = useState(false);
    const [text, setText] = useState('');
    const [anonymous, setAnonymous] = useState(false);

    const handleSubmit = (event) => {
        event.preventDefault();

        const data = {
            text,
            anonymous: allowAnonymousMessage ? anonymous : false,
            parentId: parent.id,
            episodeId: parent.episodeId,
            streamId: parent.streamId,
        };

        setSending(true);
        onSubmit(data)
            .then(() => {
                // event.target.reset();
            })
            .catch(() => {
                setSending(false);
            })
        ;
    }

    return (
        <form className="answer answerForm message" onSubmit={handleSubmit}>
            <ClientAvatar client={client} marginTop={allowAnonymousMessage ? '16px' : '0'} />
            <div className="content">
                <AnonymousMessageCheckbox
                    checked={anonymous}
                    onChange={(e) => setAnonymous(e.target.checked)}
                    enabled={allowAnonymousMessage}
                    disabled={sending}
                />
                <div className="text">
                    <div className="message-input-box">
                        <input
                            type="text"
                            name="text"
                            placeholder={$locale.get('comment.input.placeholder')}
                            autoComplete="off"
                            maxLength={maxMessageLength}
                            ref={inputRef}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            disabled={sending}
                        />
                        <s className="input-arrow-left" />
                        <input type="submit" value={$locale.get('comment.submit')} disabled={sending} />
                    </div>
                </div>
            </div>
        </form>
    );
};

const MessageComponent = ({message, allowAnswer}) => {
    const {
        $locale,
        client,
        name,
        onClickMessageTime,
        createMessage,
        likeMessage,
    } = useContext(Context);
    const [visibleAnswerForm, setVisibleAnswerForm] = useState(false);
    const [liked, setLiked] = useState(storage.get(likeStorageKey(message.id)));
    const answerInputRef = useRef(null);

    const handleLikeMessage = likeMessage ? () => {
        const active = !liked;
        likeMessage(message.id, active)
            .then((data) => {
                setLiked(active);
                if (active) {
                    storage.set(likeStorageKey(message.id), active, storage.DAY * 30);
                } else {
                    storage.remove(likeStorageKey(message.id));
                }
                return data;
            })
        ;
    } : null;

    const handleSendAnswer = (data) => {
        return createMessage(data)
            .then(() => {
                setVisibleAnswerForm(false);
            })
        ;
    };

    useEffect(() => {
        if (!visibleAnswerForm) {
            return;
        }

        answerInputRef.current?.focus();
    }, [visibleAnswerForm]);

    const children = (message.children || [])
        .filter(message => message.moderate || ((message.clientRegistration?.id ?? message.client?.id) === client?.id))
        .map(message => (
            <MessageComponent
                key={message.id}
                message={message}
                likeMessage={likeMessage}
                allowAnswer={false}
            />
        ));

    return (
        <li className={"message" + (!message.moderate ? ' unModerated' : '') + (message.fixed ? ' message-fixed' : '')}
            id={`${name}_${message.id}`}
        >
            <ClientAvatar client={message.anonymous ? null : (message.client || message.clientRegistration)}></ClientAvatar>
            <div className="content">
                <div className="title">
                    <LikeButton onClick={handleLikeMessage} active={liked}>{message.likes}</LikeButton>
                    <ClientNickname client={message.anonymous ? null : (message.client || message.clientRegistration)} />
                    <MessageTime time={message.time} onClick={() => onClickMessageTime(message)} />
                    {' '}
                    {allowAnswer ?
                        <a className="answerButton"
                           onClick={() => setVisibleAnswerForm(!visibleAnswerForm)}
                           data-bind="click: $parent.toggleAnswerForm">{$locale.get('comment.answerButton')}</a> : null}
                    {' '}
                    {message.fixed ?
                        <span className="message-fixed-hint">{$locale.get('comment.fixedMessage.hint')}</span> : null}
                </div>
                {!message.moderate ?
                    <div className="moderateLabel" data-bind="visible: !moderate()">{$locale.get('comment.moderating')}</div> : null}
                <div className="text">
                    <MessageTarget member={message.targetMember} />
                    <MessageText text={message.text} />
                </div>
            </div>
            {children.length > 0 ?
                <ul className={`answer-list`}>{children}</ul>
                : null}
            {visibleAnswerForm ?
                <AnswerForm onSubmit={handleSendAnswer} parent={message} parentId={message.id} inputRef={answerInputRef} />
                : null}
        </li>
    );
}

export default MessageComponent;
